import Inventory2Icon from '@mui/icons-material/Inventory2';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';

import { LotBreakdownDetail } from '@/types/fulfill/inventoryHistory';
import { formatDate } from '@/utils/common';

interface LotBreakdownPanelProps {
  id?: string;
  open: boolean;
  details: LotBreakdownDetail[];
  onRequestClose: () => void;
  onExited: () => void;
}

const COLUMN_TEMPLATE = '2fr 1.5fr 1fr';
const SECTION_PADDING_X = '1.5rem';

const LotBreakdownPanel = ({
  id,
  open,
  details,
  onRequestClose,
  onExited,
}: LotBreakdownPanelProps) => {
  const totalReceived = details.reduce((sum, lot) => sum + lot.quantityReceived, 0);

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest('.lot-breakdown-toggle')) return;
        onRequestClose();
      }}
    >
      <Collapse in={open} appear timeout={250} onExited={onExited} sx={{ width: '100%' }}>
        <Box id={id} role="region" sx={{ width: '100%', textAlign: 'left', my: '0.375rem' }}>
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              px: SECTION_PADDING_X,
              py: '0.75rem',
              backgroundColor: (theme) => theme.palette.primary.lighter,
              borderTop: (theme) => `1px solid ${theme.palette.grey[200]}`,
              borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            <Inventory2Icon color="primary" sx={{ fontSize: '1.35rem' }} />
            <Typography
              variant="body2"
              sx={{ fontWeight: 'bold', color: (theme) => theme.palette.grey[700] }}
            >
              Lotes de la orden
            </Typography>
          </Box>

          {details.length === 0 ? (
            <Box
              sx={{
                px: SECTION_PADDING_X,
                py: '1rem',
                backgroundColor: (theme) => theme.palette.common.white,
                borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}`,
              }}
            >
              <Typography variant="body2" sx={{ color: (theme) => theme.palette.grey[600] }}>
                Esta orden no tiene lotes generados todavía.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box
                display="grid"
                gridTemplateColumns={COLUMN_TEMPLATE}
                sx={{
                  px: SECTION_PADDING_X,
                  py: 0.75,
                  borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ color: (theme) => theme.palette.secondary.main }}
                >
                  Lote
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ color: (theme) => theme.palette.secondary.main }}
                >
                  Fecha de caducidad
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ textAlign: 'right', color: (theme) => theme.palette.primary.main }}
                >
                  Recibido
                </Typography>
              </Box>

              {details.map((lot) => (
                <Box
                  key={lot.lotId}
                  display="grid"
                  gridTemplateColumns={COLUMN_TEMPLATE}
                  sx={{
                    px: SECTION_PADDING_X,
                    py: 0.75,
                    backgroundColor: (theme) => theme.palette.common.white,
                    borderBottom: (theme) => `1px solid ${theme.palette.grey[100]}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: (theme) => theme.palette.secondary.main }}
                  >
                    {lot.lotName}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: (theme) => theme.palette.secondary.main }}
                  >
                    {formatDate(lot.expiresAt, 'dd/MM/yyyy')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ textAlign: 'right', color: (theme) => theme.palette.secondary.main }}
                  >
                    {lot.quantityReceived}
                  </Typography>
                </Box>
              ))}

              <Box
                display="grid"
                gridTemplateColumns={COLUMN_TEMPLATE}
                sx={{
                  px: SECTION_PADDING_X,
                  py: 1.25,
                  backgroundColor: (theme) => theme.palette.grey[50],
                  borderTop: (theme) => `2px solid ${theme.palette.primary.main}`,
                  borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ color: (theme) => theme.palette.secondary.main }}
                >
                  Totales
                </Typography>
                <Typography variant="body2" />
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ textAlign: 'right', color: (theme) => theme.palette.secondary.main }}
                >
                  {totalReceived}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </ClickAwayListener>
  );
};

export default LotBreakdownPanel;
